import { ref, toRefs, reactive } from 'vue'
import sleep from 'src/utils/sleep'

type Action = {
  btnText: string
  onClick?: () => void
  [key: string]: any // Любые пропсы v-btn
}

type SnackbarOptions = {
  text: string
  snackbar?: Record<string, any> // Любые пропсы v-snackbar
  action?: Action
}

const isVisible = ref(false)

const state = reactive({
  text: '',
  snackbar: {} as Record<string, any>,
  action: undefined as Action | undefined,
})

/**
 * useSnackbar — глобальный composable для управления snackbar-уведомлениями.
 *
 * @example
 * // Простой вариант
 * snackbar.show('Сохранено')
 *
 * @example
 * // Расширенный вариант с настройками
 * snackbar.show({
 *   text: 'Данные сохранены',
 *   snackbar: { color: 'primary', timeout: 10000 },
 *   action: { btnText: 'Отмена', onClick: () => snackbar.hide() },
 * })
 */
export default function useSnackbar() {
  async function show(input: SnackbarOptions | string) {
    await hide()

    const isString = typeof input === 'string'

    state.text = isString ? input : input.text

    state.snackbar = {
      color: 'text',
      ...(isString ? {} : input.snackbar),
    }

    state.action = isString
      ? undefined
      : input.action && {
        height: 40,
        text: true,
        color: 'button-text-snackbars',
        ...input.action,
      }

    state.snackbar.timeout ??= state.action ? 7000 : 4000

    isVisible.value = true
  }

  async function hide() {
    if (!isVisible.value) { return }

    isVisible.value = false
    await sleep(100) // Добавляет эффект обновления snackbar при множественном открытии

    state.text = ''
    state.snackbar = {}
    state.action = undefined
  }

  function actionClick() {
    if (state.action?.onClick) {
      state.action.onClick()
    } else {
      hide()
    }
  }

  return {
    ...toRefs(state),
    isVisible,
    show,
    hide,
    actionClick,
  }
}
